/**
 * Which one notice the terminal shows, and why.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE AT A TIME, IN PRIORITY ORDER
 *
 * The screen used to stack two independent banners: "the feed is offline" and
 * "you have not nominated a feed account". Both could be true at once, and
 * when they were, the second was the cause of the first — so the trader read
 * an alarming sentence about a disconnection above the sentence explaining
 * that nothing was ever going to connect.
 *
 * So the states are ordered by what the reader should do about them, and the
 * first match wins:
 *
 *   EXECUTION DEGRADED  orders are being refused — and this is FIRST,
 *                       above everything below it, because the rest of this
 *                       ladder is about PRICES. A trader can read a stale
 *                       chain; they cannot trade through an account the
 *                       broker has stopped accepting orders from, and until
 *                       this rung existed nothing on this screen said so.
 *   NO FEED ACCOUNT   nothing will ever arrive — fix it on another screen
 *   MARKET CLOSED     nothing is wrong; these are closing prices
 *   CONNECTING        it is in progress; wait
 *   OFFLINE           it should be working and is not
 *   SILENT            connected, and nothing has come down it
 *   UNPRICED          connected, ticking, and every contract is a dash
 *
 * PURE, so the ladder can be tested without a socket. The component does
 * nothing but paint what this returns.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A CLOSED MARKET IS NOT AN ERROR, AND IS NOT PAINTED LIKE ONE
 *
 * Outside 09:15–15:40 the socket is deliberately asleep and the prices on
 * screen are the last close. That is correct behaviour. Painting it red —
 * which the old banner did, because it only knew "not live" — trains a
 * trader to ignore the colour that means their feed has actually died.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import type { NoticeTone } from '@/components/Notice'
import type { FeedState } from './feedState'

export type TerminalNoticeKind =
  /** The account orders go to cannot place them — 2.7. */
  | 'EXECUTION_DEGRADED'
  | 'NO_FEED_ACCOUNT'
  | 'NO_BROKER'
  | 'MARKET_CLOSED'
  | 'CONNECTING'
  | 'OFFLINE'
  | 'SILENT'
  | 'UNPRICED'

export interface TerminalNotice {
  kind: TerminalNoticeKind
  tone: NoticeTone
  title: string
  /** The sentence after the title. Plain text; the caller may decorate it. */
  body: string
  /** True when a Reconnect button belongs on this notice. */
  canReconnect: boolean
  /** True when the fix is on the Accounts screen. */
  linkAccounts: boolean
}

export interface TerminalNoticeInput {
  /**
   * The account the next order goes to, when it is not order-capable — 2.7.
   *
   * `why` is the BROKER'S OWN SENTENCE, carried from `lastError` rather than
   * reworded here. On a dead session the engine writes "Session expired: …"
   * when an order discovers it, which is the one case this was built for and
   * the one a trader can fix in ten seconds.
   *
   * Deliberately not keyed on a parsed reason: DEGRADED covers several
   * refusals and all of them mean the same thing to someone trying to
   * trade, so the rung is general and the sentence is specific.
   */
  executionAccount?: { label: string; why: string | null } | null
  feed: FeedState
  socketState: string
  /** False while the account list is loading — say nothing until then. */
  accountsLoaded: boolean
  /** How many broker accounts this login has. */
  accountCount: number
  /** True once one of them is nominated as the feed. */
  hasFeedAccount: boolean
  /** True when at least one account has market-data credentials to nominate. */
  canNominateFeed: boolean
  /** Contracts currently on screen, and how many of them have a price. */
  chain?: { shown: number; priced: number }
}

/**
 * The notice to show, or null when there is nothing to say.
 *
 * Null is the normal case during a live session, and the normal case is the
 * one that must stay silent: a strip that is always there is furniture.
 */
export function terminalNoticeOf(input: TerminalNoticeInput): TerminalNotice | null {
  const { feed, socketState, accountsLoaded, accountCount, hasFeedAccount } = input

  /*
   * ── ORDERS BEFORE PRICES ────────────────────────────────────────────────
   * Above the feed rungs on purpose. A stale chain is readable; an account
   * the broker refuses is not tradeable, and that is the more urgent of the
   * two to put in front of someone mid-session.
   */
  if (input.executionAccount) {
    return {
      kind: 'EXECUTION_DEGRADED',
      tone: 'error',
      title: `${input.executionAccount.label} cannot place orders.`,
      body:
        (input.executionAccount.why ??
          'The broker refused this account on its last order.') +
        ' Reconnect it from Accounts — prices are unaffected.',
      canReconnect: false,
      linkAccounts: true,
    }
  }

  // ── nothing will ever arrive ────────────────────────────────────────────
  if (accountsLoaded && !hasFeedAccount) {
    if (accountCount === 0) {
      return {
        kind: 'NO_BROKER',
        tone: 'warn',
        title: 'No broker connected.',
        body: 'Connect a broker account and give it market-data credentials to start the price feed.',
        canReconnect: false,
        linkAccounts: true,
      }
    }
    return {
      kind: 'NO_FEED_ACCOUNT',
      tone: 'warn',
      title: 'No live prices.',
      body: input.canNominateFeed
        ? 'Choose which account carries your feed — your broker allows one streaming session per market-data key, so exactly one account can.'
        : 'Add market-data credentials to one of your accounts and make it your feed. Order credentials alone place trades; they do not stream prices.',
      canReconnect: false,
      linkAccounts: true,
    }
  }

  // ── nothing is wrong ────────────────────────────────────────────────────
  if (feed.kind === 'CLOSED') {
    return {
      kind: 'MARKET_CLOSED',
      tone: 'warn',
      title: 'Market is closed.',
      body: 'The prices below are the last close. Live updates resume at 09:15 on the next trading day.',
      canReconnect: false,
      linkAccounts: false,
    }
  }

  // ── in progress ─────────────────────────────────────────────────────────
  if (feed.kind === 'OFFLINE' && (socketState === 'CONNECTING' || socketState === 'RECONNECTING')) {
    return {
      kind: 'CONNECTING',
      tone: 'info',
      title: 'Connecting to the live feed…',
      body: 'Prices are frozen until the first frame arrives.',
      canReconnect: false,
      linkAccounts: false,
    }
  }

  // ── it should be working and is not ─────────────────────────────────────
  if (feed.kind === 'OFFLINE') {
    if (socketState === 'OPEN') {
      /*
       * The state that must never read as live: a socket in OPEN with
       * nothing coming down it. It is how a frozen price gets traded
       * against, so it is said plainly rather than left to the age chip.
       */
      return {
        kind: 'SILENT',
        tone: 'error',
        title: 'The feed is connected but silent.',
        body:
          feed.lastTickAt === null
            ? 'No market data has arrived in this session. Prices are frozen, and new orders will be refused with FEED_STALE.'
            : 'No frame has arrived for several seconds. Prices are frozen, and new orders will be refused with FEED_STALE.',
        canReconnect: true,
        linkAccounts: false,
      }
    }
    return {
      kind: 'OFFLINE',
      tone: 'error',
      title: 'Market feed is offline.',
      body: 'Prices are frozen, so new orders will be refused with FEED_STALE until it reconnects.',
      canReconnect: true,
      linkAccounts: false,
    }
  }

  // ── live, but saying nothing useful ─────────────────────────────────────
  if (input.chain && input.chain.shown > 0 && input.chain.priced === 0) {
    return {
      kind: 'UNPRICED',
      tone: 'warn',
      title: 'Connected, but nothing is priced.',
      body: 'The feed is live and none of the contracts on screen has a price yet — usually a subscription that has not been acknowledged. Reconnect if it persists.',
      canReconnect: true,
      linkAccounts: false,
    }
  }

  return null
}
